import { EmptyState, Skeleton } from '@mevaser/ui'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { ArrowLeft, MessagesSquare } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { getProactiveSite } from '../components/support-widget.js'

const HOST_SELECTOR = '[data-proactivesite-root]'
const LAUNCHER_SELECTOR = '[data-proactivesite-launcher]'
const FULL_PAGE_ATTR = 'data-mv-full-page'
// The identity fetch, the loader script, the embed's own load and its admin
// gate all have to finish before the panel exists — generous, since giving up
// too early would show "not available" to someone whose connection is just slow.
const WAIT_FOR_HOST_MS = 8000

type Status = 'waiting' | 'active' | 'unavailable'

/**
 * The support chat, expanded to fill the page.
 *
 * There is no chat UI to render here: the conversation lives in the vendor's
 * iframe (see support-widget.tsx), already built and sitting in the DOM once
 * the assistant has loaded. This page finds that panel and stretches it over
 * a placeholder area via the data-mv-full-page CSS in app.css — the same
 * iframe, same conversation, just resized. Closing it (the embed's own
 * button, or Escape) is treated as "leave this page".
 */
export function SupportChatPage() {
  const { from } = useSearch({ from: '/authed/support-chat' })
  const navigate = useNavigate()
  const backHref = from && from !== '/support-chat' ? from : '/'
  const [status, setStatus] = useState<Status>('waiting')
  const placeholderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    let host: HTMLElement | null = null
    let wasOpen = false
    let timeoutId: number | undefined

    const applyGeometry = () => {
      try {
        const el = placeholderRef.current
        if (!host || !el) return
        const r = el.getBoundingClientRect()
        host.style.setProperty('--mv-sc-top', `${Math.max(12, r.top)}px`)
        host.style.setProperty('--mv-sc-left', `${Math.max(12, r.left)}px`)
        host.style.setProperty('--mv-sc-right', `${Math.max(12, window.innerWidth - r.right)}px`)
      } catch {
        // A nicety; the panel just keeps its previous geometry.
      }
    }

    const styleObserver = new MutationObserver(() => {
      try {
        // The only signal acted on: the embed's own close button posts
        // "close" to the loader, and Escape calls it directly — both just
        // flip this inline style. Either one means leave the page.
        if (host && host.style.display === 'none') void navigate({ href: backHref })
      } catch {
        // ignore
      }
    })

    const resizeObserver = new ResizeObserver(applyGeometry)
    const onWindowChange = () => applyGeometry()

    const enter = (h: HTMLElement) => {
      try {
        host = h
        wasOpen = h.style.display === 'block'
        applyGeometry()
        h.setAttribute(FULL_PAGE_ATTR, '')
        getProactiveSite()?.open?.()
        styleObserver.observe(h, { attributes: true, attributeFilter: ['style'] })
        if (placeholderRef.current) resizeObserver.observe(placeholderRef.current)
        window.addEventListener('resize', onWindowChange)
        window.addEventListener('scroll', onWindowChange, true)
        setStatus('active')
      } catch {
        setStatus('unavailable')
      }
    }

    const bodyObserver = new MutationObserver(() => {
      try {
        const h = document.querySelector(HOST_SELECTOR)
        if (!host && h instanceof HTMLElement) {
          clearTimeout(timeoutId)
          enter(h)
        } else if (host && !host.isConnected) {
          // The vendor's admin gate resolved to "not an administrator" and
          // tore the panel out of the DOM while we were on this page.
          host = null
          setStatus('unavailable')
        }
      } catch {
        // ignore
      }
    })

    try {
      bodyObserver.observe(document.body, { childList: true })
      const existing = document.querySelector(HOST_SELECTOR)
      if (existing instanceof HTMLElement) {
        enter(existing)
      } else {
        timeoutId = window.setTimeout(() => {
          if (!host && !cancelled) setStatus('unavailable')
        }, WAIT_FOR_HOST_MS)
      }
    } catch {
      setStatus('unavailable')
    }

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
      try {
        // Disconnect first: a mutation record queued after disconnect() is
        // dropped, so the close() call below can never bounce this cleanup
        // back into navigate() via the style observer.
        styleObserver.disconnect()
        bodyObserver.disconnect()
        resizeObserver.disconnect()
        window.removeEventListener('resize', onWindowChange)
        window.removeEventListener('scroll', onWindowChange, true)
        if (host?.isConnected) {
          if (wasOpen) {
            // Re-anchor to the launcher rather than leaving it anchored to
            // wherever it last was in full-page mode.
            const launcher = document.querySelector(LAUNCHER_SELECTOR)
            getProactiveSite()?.open?.(launcher instanceof HTMLElement ? launcher : undefined)
          } else {
            getProactiveSite()?.close?.()
          }
          host.removeAttribute(FULL_PAGE_ATTR)
          host.style.removeProperty('--mv-sc-top')
          host.style.removeProperty('--mv-sc-left')
          host.style.removeProperty('--mv-sc-right')
        }
      } catch {
        // Leaving the page must never throw.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backHref, navigate])

  return (
    <Page>
      <div className="pb-3">
        {/* A real anchor, not <Link>: `backHref` is an arbitrary in-app path
            from the `from` search param, which the typed `to` prop can't
            express. The href keeps middle-click/keyboard open-in-new-tab
            working; the click handler intercepts a plain click for SPA
            navigation, same as navigate({ href }) used below. */}
        <a
          href={backHref}
          onClick={(e) => {
            e.preventDefault()
            void navigate({ href: backHref })
          }}
          className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" /> Back
        </a>
      </div>

      <PageHeader
        title="Support assistant"
        description="The support chat, expanded to a full page. Closing it here returns you to where you were."
      />

      <div ref={placeholderRef} className="min-h-64">
        {status === 'waiting' ? <Skeleton className="h-64 w-full" /> : null}
        {status === 'unavailable' ? (
          <EmptyState
            icon={MessagesSquare}
            title="Assistant not available"
            description="The support assistant isn't enabled here, or this account doesn't have access to it."
            action={{ label: 'Go back', onClick: () => void navigate({ href: backHref }) }}
          />
        ) : null}
        {/* status === 'active': the vendor panel overlays this area. */}
      </div>
    </Page>
  )
}
